export interface EvolutionCredentials {
  baseUrl: string;
  apiKey: string;
}

export interface EvolutionCreateInstancePayload {
  instanceName: string;
  token?: string;
  number?: string;
  qrcode: boolean;
  integration: 'WHATSAPP-BAILEYS' | 'WHATSAPP-BUSINESS' | 'EVOLUTION';
}

export interface EvolutionSetWebhookPayload {
  enabled: boolean;
  url: string;
  webhookByEvents: boolean;
  webhookBase64: boolean;
  events: string[];
}

export interface EvolutionSetSettingsPayload {
  rejectCall: boolean;
  msgCall: string;
  groupsIgnore: boolean;
  alwaysOnline: boolean;
  readMessages: boolean;
  readStatus: boolean;
  syncFullHistory: boolean;
}

const normalizeBaseUrl = (baseUrl: string) => baseUrl.trim().replace(/\/+$/, '');

const toErrorMessage = (status: number, body: unknown): string => {
  if (body && typeof body === 'object') {
    const payload = body as Record<string, unknown>;
    const nested = payload.response as Record<string, unknown> | undefined;
    const messages = nested?.message;
    if (Array.isArray(messages) && messages.length > 0) {
      return `${status} - ${String(messages[0])}`;
    }
    const candidates = [payload.message, payload.error, payload.detail, nested?.error];
    for (const item of candidates) {
      if (typeof item === 'string' && item.trim()) return `${status} - ${item.trim()}`;
    }
  }
  if (typeof body === 'string' && body.trim()) return `${status} - ${body.trim()}`;
  return `${status} - Erro ao chamar Evolution API.`;
};

const requestEvolution = async <T>(
  credentials: EvolutionCredentials,
  path: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetch(`${normalizeBaseUrl(credentials.baseUrl)}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      apikey: credentials.apiKey.trim(),
      Authorization: `Bearer ${credentials.apiKey.trim()}`,
      'x-api-key': credentials.apiKey.trim(),
      ...(options.headers || {})
    }
  });

  const text = await response.text().catch(() => '');
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    throw new Error(toErrorMessage(response.status, parsed ?? text));
  }

  return (parsed as T) ?? ({} as T);
};

export const evolutionService = {
  async createInstance(credentials: EvolutionCredentials, payload: EvolutionCreateInstancePayload) {
    return requestEvolution<Record<string, unknown>>(credentials, '/instance/create', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  async fetchInstances(credentials: EvolutionCredentials) {
    return requestEvolution<unknown[]>(credentials, '/instance/fetchInstances', {
      method: 'GET'
    });
  },

  async connectInstance(credentials: EvolutionCredentials, instance: string, number?: string) {
    const query = number?.trim() ? `?number=${encodeURIComponent(number.trim())}` : '';
    return requestEvolution<Record<string, unknown>>(credentials, `/instance/connect/${encodeURIComponent(instance)}${query}`, {
      method: 'GET'
    });
  },

  async getConnectionState(credentials: EvolutionCredentials, instance: string) {
    return requestEvolution<Record<string, unknown>>(credentials, `/instance/connectionState/${encodeURIComponent(instance)}`, {
      method: 'GET'
    });
  },

  async deleteInstance(credentials: EvolutionCredentials, instance: string) {
    return requestEvolution<Record<string, unknown>>(credentials, `/instance/delete/${encodeURIComponent(instance)}`, {
      method: 'DELETE'
    });
  },

  async setWebhook(credentials: EvolutionCredentials, instance: string, payload: EvolutionSetWebhookPayload) {
    const normalized = {
      enabled: payload.enabled,
      url: payload.url,
      webhookByEvents: payload.webhookByEvents,
      webhookBase64: payload.webhookBase64,
      events: payload.events
    };

    try {
      // Evolution v2 (alguns builds) exige objeto "webhook" dentro do body.
      return await requestEvolution<Record<string, unknown>>(credentials, `/webhook/set/${encodeURIComponent(instance)}`, {
        method: 'POST',
        body: JSON.stringify({
          webhook: normalized
        })
      });
    } catch (error) {
      // Fallback para builds que aceitam payload direto.
      return requestEvolution<Record<string, unknown>>(credentials, `/webhook/set/${encodeURIComponent(instance)}`, {
        method: 'POST',
        body: JSON.stringify(normalized)
      });
    }
  },

  async findWebhook(credentials: EvolutionCredentials, instance: string) {
    return requestEvolution<Record<string, unknown>>(credentials, `/webhook/find/${encodeURIComponent(instance)}`, {
      method: 'GET'
    });
  },

  async setSettings(credentials: EvolutionCredentials, instance: string, payload: EvolutionSetSettingsPayload) {
    return requestEvolution<Record<string, unknown>>(credentials, `/settings/set/${encodeURIComponent(instance)}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  async findSettings(credentials: EvolutionCredentials, instance: string) {
    return requestEvolution<Record<string, unknown>>(credentials, `/settings/find/${encodeURIComponent(instance)}`, {
      method: 'GET'
    });
  }
};
